/** Import candidates from Excel / CSV — opened from the Recruitment Network toolbar and the
 *  requirement page. One shared review modal, three steps:
 *
 *  1. Pick a file. It is read HERE, in the browser (packages/shared candidateImport.ts) — only the
 *     columns of the rows the recruiter keeps ever reach the server: name, email, and the detail
 *     columns it recognises (phone, experience, notice, last working day, CTC, location) plus any
 *     other headed column, kept as-is.
 *  2. Review. Every row is shown with a status; people already on this requirement and repeats inside
 *     the file are flagged and unticked (the alert at the top says so), bad rows can be fixed in place.
 *     Select all, filter, search, delete one or delete the selected.
 *  3. Send: POST /requisitions/{id}/candidates/import adds them without an interview (free) and emails
 *     each the "complete your application" link for their CV. An import never sends the interview
 *     link (6 Oct 2026): the interview follows the CV, by the requirement's CV screen — set here —
 *     or a recruiter's Invite on the requirement page. */
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ChangeEvent, DragEvent, ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Mail, Search, Trash2, UploadCloud, X } from "lucide-react";
import {
  CV_SCREEN_DEFAULT_THRESHOLD,
  guessColumns,
  IMPORT_FIELD_LABEL,
  IMPORT_FIELDS,
  ImportFileError,
  isImportEmail,
  readSheet,
  toImportRows,
  type ColumnGuess,
  type CvScreen,
  type CvScreenMode,
  type ImportField,
  type ImportRow,
  type SheetRows,
} from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { serverMessage } from "./planUsage.js";
import { displayTitle } from "./displayTitle.js";
import "../../styles/import-candidates.css";

/** The check endpoint's limit; a longer file is trimmed with a note. */
const MAX_ROWS = 1000;
/** Both add endpoints take 200 people per request. */
const CHUNK = 200;

type Filter = "all" | "ready" | "existing" | "duplicate" | "fix";
type Status = "ready" | "cv" | "existing" | "duplicate" | "noName" | "badEmail";

interface Row extends Omit<ImportRow, "line"> {
  id: number;
  line: number;
}

interface ExistingMatch {
  email: string;
  kind: "candidate" | "resume";
  name: string | null;
  state: string | null;
}

interface Outcome {
  done: number;
  skipped: number;
  screen: CvScreen;
}

const SCREEN_LABEL: Record<CvScreenMode, { title: string; help: string }> = {
  suggest: {
    title: "Score it, I’ll approve the interviews",
    help: "Each CV is scored against this requirement as it arrives (one resume scan each). Those at or above the bar wait for you under “Ready to invite”.",
  },
  auto: {
    title: "Score it and send the interview automatically",
    help: "Each CV is scored as it arrives (one resume scan each). At or above the bar, the AI interview link is emailed straight away (one AI interview each).",
  },
  off: {
    title: "Don’t score — I’ll review CVs myself",
    help: "CVs land on the requirement unscored. You run matching and invite when you choose.",
  },
};

/** "5 yrs · 30 d notice · ₹12 LPA → ₹16 LPA" — the row's details at a glance. */
function detailSummary(r: Row): string {
  const bits: string[] = [];
  if (r.experienceYears !== undefined) bits.push(`${r.experienceYears} yrs`);
  if (r.noticePeriodDays !== undefined) bits.push(r.noticePeriodDays === 0 ? "Immediate" : `${r.noticePeriodDays} d notice`);
  if (r.lastWorkingDate) bits.push(`LWD ${r.lastWorkingDate}`);
  if (r.currentCtc || r.expectedCtc) bits.push([r.currentCtc, r.expectedCtc].filter(Boolean).join(" → "));
  if (r.location) bits.push(r.location);
  if (r.phone) bits.push(r.phone);
  const extra = Object.keys(r.extra ?? {}).length;
  if (extra) bits.push(`+${extra} more`);
  return bits.join(" · ");
}

const STATUS_LABEL: Record<Status, string> = {
  ready: "New",
  cv: "CV on file",
  existing: "Already added",
  duplicate: "Repeated in file",
  noName: "Needs a name",
  badEmail: "Check email",
};

const colName = (i: number) => {
  let s = "";
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
};

const chunks = <T,>(a: T[], n: number): T[][] => Array.from({ length: Math.ceil(a.length / n) }, (_, i) => a.slice(i * n, i * n + n));

export function ImportCandidatesModal({
  reqId,
  reqTitle,
  onClose,
}: {
  reqId: string;
  reqTitle: string;
  onClose: () => void;
}): ReactNode {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [sheet, setSheet] = useState<SheetRows | null>(null);
  const [cols, setCols] = useState<ColumnGuess | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [fileError, setFileError] = useState<string | null>(null);
  const [trimmed, setTrimmed] = useState(0);
  const [reading, setReading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [needsProfile, setNeedsProfile] = useState(true);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [showCols, setShowCols] = useState(false);
  // Other headed columns are kept by default (the team asked for everything in the file); one switch
  // drops them all, so nothing beyond the mapped fields leaves this browser unless the recruiter wants it.
  const [keepExtra, setKeepExtra] = useState(true);

  // The requirement's CV screen as it stands — the choice below starts from it. A requirement that
  // has never had one starts on "score, I approve": the recruiter sees the choice before sending.
  const reqQ = useQuery({
    queryKey: ["requisition", reqId],
    queryFn: () => api.get<{ requisition: { config?: { cvScreen?: CvScreen } } }>(`/requisitions/${reqId}`),
    staleTime: 30_000,
  });
  const [screenPick, setScreenPick] = useState<CvScreen | null>(null);
  const screen: CvScreen = screenPick ?? reqQ.data?.requisition.config?.cvScreen ?? { mode: "suggest", threshold: CV_SCREEN_DEFAULT_THRESHOLD };

  const title = displayTitle(reqTitle);

  /** Rebuild the row list from the sheet with a column choice; every selectable row starts ticked. */
  const load = (data: SheetRows, g: ColumnGuess, preselect = true): void => {
    const all = toImportRows(data, g);
    setTrimmed(Math.max(0, all.length - MAX_ROWS));
    const next = all.slice(0, MAX_ROWS).map((r, i) => ({ id: i + 1, ...r }));
    setRows(next);
    if (preselect) setSelected(new Set(next.map((r) => r.id)));
  };

  const pickFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setFileError(null);
    setOutcome(null);
    setReading(true);
    try {
      const data = await readSheet(file);
      const g = guessColumns(data);
      if (g.emailCol < 0) throw new ImportFileError("No email column found — add a column headed “Email” and try again.");
      setFileName(file.name);
      setSheet(data);
      setCols(g);
      load(data, g);
    } catch (err) {
      setFileError(err instanceof ImportFileError ? err.message : "Couldn’t read that file — save it as .xlsx or .csv and try again.");
    } finally {
      setReading(false);
    }
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0];
    e.target.value = "";
    void pickFile(f);
  };
  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    setDragging(false);
    void pickFile(e.dataTransfer.files?.[0]);
  };

  // ── Who is already on this requirement? Re-asked whenever the set of valid emails changes. ──
  const validEmails = useMemo(
    () => [...new Set(rows.map((r) => r.email).filter(isImportEmail))].sort(),
    [rows],
  );
  const check = useQuery({
    queryKey: ["importCheck", reqId, validEmails.join("|")],
    enabled: validEmails.length > 0,
    staleTime: 30_000,
    queryFn: async () =>
      (await api.post<{ existing: ExistingMatch[] }>(`/requisitions/${reqId}/candidates/check`, { emails: validEmails }))
        .existing,
  });
  const existingBy = useMemo(() => new Map((check.data ?? []).map((m) => [m.email, m])), [check.data]);

  const statusOf = useMemo(() => {
    const firstSeen = new Map<string, number>();
    for (const r of rows) if (r.email && !firstSeen.has(r.email)) firstSeen.set(r.email, r.id);
    const out = new Map<number, Status>();
    for (const r of rows) {
      const m = existingBy.get(r.email);
      out.set(
        r.id,
        !isImportEmail(r.email)
          ? "badEmail"
          : m?.kind === "candidate"
            ? "existing"
            : firstSeen.get(r.email) !== r.id
              ? "duplicate"
              : !r.name.trim()
                ? "noName"
                : m?.kind === "resume"
                  ? "cv"
                  : "ready",
      );
    }
    return out;
  }, [rows, existingBy]);

  const selectable = (id: number) => {
    const s = statusOf.get(id);
    return s === "ready" || s === "cv";
  };
  // A row that became unselectable (its email turned out to be on the requirement) drops out.
  const chosen = rows.filter((r) => selected.has(r.id) && selectable(r.id));

  const counts = useMemo(() => {
    const c = { all: rows.length, ready: 0, existing: 0, duplicate: 0, fix: 0 };
    for (const r of rows) {
      const s = statusOf.get(r.id)!;
      if (s === "ready" || s === "cv") c.ready++;
      else if (s === "existing") c.existing++;
      else if (s === "duplicate") c.duplicate++;
      else c.fix++;
    }
    return c;
  }, [rows, statusOf]);

  const q = search.trim().toLowerCase();
  const visible = rows.filter((r) => {
    const s = statusOf.get(r.id)!;
    const f =
      filter === "all" ||
      (filter === "ready" && (s === "ready" || s === "cv")) ||
      (filter === "existing" && s === "existing") ||
      (filter === "duplicate" && s === "duplicate") ||
      (filter === "fix" && (s === "noName" || s === "badEmail"));
    return f && (!q || r.name.toLowerCase().includes(q) || r.email.includes(q));
  });
  const visibleSelectable = visible.filter((r) => selectable(r.id));
  const allVisibleOn = visibleSelectable.length > 0 && visibleSelectable.every((r) => selected.has(r.id));

  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleAll = () =>
    setSelected((s) => {
      const n = new Set(s);
      for (const r of visibleSelectable) {
        if (allVisibleOn) n.delete(r.id);
        else n.add(r.id);
      }
      return n;
    });
  const remove = (ids: number[]) => {
    const gone = new Set(ids);
    setRows((rs) => rs.filter((r) => !gone.has(r.id)));
    setSelected((s) => new Set([...s].filter((id) => !gone.has(id))));
  };
  const edit = (id: number, patch: Partial<Pick<Row, "name" | "email">>) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    // Fixing a row makes it addable — tick it so the fix is not silently left out.
    setSelected((s) => new Set(s).add(id));
  };

  const refresh = (): void => {
    void qc.invalidateQueries({ predicate: (query) => query.queryKey.includes(reqId) });
    void qc.invalidateQueries({ queryKey: ["requisitions"] });
  };

  // Everything the file said about each kept row; empty values never leave the browser.
  const people = chosen.map(({ id: _id, line: _line, extra, ...r }) => ({
    ...r,
    name: r.name.trim(),
    ...(keepExtra && extra ? { extra } : {}),
  }));

  const requestDetails = useMutation({
    mutationFn: async () => {
      let done = 0;
      let skipped = 0;
      let first = true;
      for (const part of chunks(people, CHUNK)) {
        const res = await api.post<{ requested: unknown[]; skipped: unknown[] }>(`/requisitions/${reqId}/candidates/import`, {
          candidates: part,
          needsResume: true,
          needsProfile,
          ...(fileName ? { sourceFile: fileName } : {}),
          // The requirement's CV screen, saved once with the first part.
          ...(first ? { cvScreen: screen } : {}),
        });
        first = false;
        done += res.requested.length;
        skipped += res.skipped.length;
      }
      return { done, skipped };
    },
    onSuccess: (r) => {
      refresh();
      setOutcome({ ...r, screen });
    },
  });

  const busy = requestDetails.isPending;
  const submit = (): void => {
    if (chosen.length) requestDetails.mutate();
  };

  const reset = (): void => {
    setFileName(null);
    setSheet(null);
    setCols(null);
    setRows([]);
    setSelected(new Set());
    setOutcome(null);
    setFilter("all");
    setSearch("");
    requestDetails.reset();
  };

  const header = sheet && cols && cols.headerRow >= 0 ? sheet[cols.headerRow] ?? [] : [];
  const width = sheet ? Math.max(0, ...sheet.map((r) => r.length)) : 0;
  const colOptions = Array.from({ length: width }, (_, i) => ({
    value: i,
    label: header[i] ? `${colName(i)} · ${header[i]}` : `Column ${colName(i)}`,
  }));
  const setCol = (key: "nameCol" | "emailCol", v: number) => {
    if (!sheet || !cols) return;
    const g = { ...cols, [key]: v, ...(key === "nameCol" ? { lastNameCol: -1 } : {}) };
    setCols(g);
    load(sheet, g, false);
  };
  const setFieldCol = (field: ImportField, v: number) => {
    if (!sheet || !cols) return;
    const g = { ...cols, fields: { ...cols.fields, [field]: v } };
    setCols(g);
    // Re-read the details without losing the recruiter's ticks or in-place name/email fixes.
    const byLine = new Map(toImportRows(sheet, g).map((r) => [r.line, r]));
    setRows((rs) =>
      rs.map((r) => {
        const fresh = byLine.get(r.line);
        if (!fresh) return r;
        const { name: _n, email: _e, line: _l, ...details } = fresh;
        return { id: r.id, line: r.line, name: r.name, email: r.email, ...details };
      }),
    );
  };
  const foundFields = IMPORT_FIELDS.filter((f) => (cols?.fields?.[f] ?? -1) >= 0);
  const extraCount = new Set(rows.flatMap((r) => Object.keys(r.extra ?? {}))).size;

  const error = requestDetails.isError
    ? serverMessage(requestDetails.error, "Couldn’t add those candidates — please try again.")
    : null;

  // Portalled to <body>: the network view renders inside `.rn`, a fixed layer that is its own
  // stacking context (z 1), so a modal left in place would sit UNDER the app's top bar and bottom nav.
  return createPortal(
    <>
      <div className="ic-scrim" role="dialog" aria-modal="true" aria-labelledby="ic-title" data-no-pan onClick={busy ? undefined : onClose}>
        <div className={`ic-modal${rows.length ? " is-wide" : ""}`} onClick={(e) => e.stopPropagation()}>
          <header className="ic-head">
            <div>
              <h2 id="ic-title">Import candidates</h2>
              <p>
                to <b>{title}</b>
                {fileName && (
                  <>
                    {" "}· <FileSpreadsheet size={13} aria-hidden="true" /> {fileName}
                  </>
                )}
              </p>
            </div>
            <button type="button" className="ic-icon-btn" onClick={onClose} disabled={busy} aria-label="Close">
              <X size={16} />
            </button>
          </header>

          {outcome ? (
            <div className="ic-body ic-done">
              <CheckCircle2 size={34} aria-hidden="true" />
              <h3>{`${outcome.done} candidate${outcome.done === 1 ? "" : "s"} asked for their CV`}</h3>
              <p>
                Each one was emailed a link to send their CV{needsProfile ? " and details" : ""} — nothing was charged.{" "}
                {outcome.screen.mode === "auto"
                  ? `As CVs come back they’re scored; at ${outcome.screen.threshold}% or more the interview link goes out automatically.`
                  : outcome.screen.mode === "suggest"
                    ? `As CVs come back they’re scored; at ${outcome.screen.threshold}% or more they wait for you under “Ready to invite” on the requirement.`
                    : "CVs will land on the requirement unscored for you to review."}{" "}
                Track who has replied under “Imports & CVs” on the requirement page.
                {outcome.skipped > 0 &&
                  ` ${outcome.skipped} ${outcome.skipped === 1 ? "was" : "were"} already on this requirement and ${outcome.skipped === 1 ? "was" : "were"} left as they were.`}
              </p>
              <footer className="ic-foot">
                <button type="button" className="ic-btn" onClick={reset}>Import another file</button>
                <button type="button" className="ic-btn primary" onClick={onClose}>Done</button>
              </footer>
            </div>
          ) : !rows.length ? (
            <div className="ic-body">
              <button
                type="button"
                className={`ic-drop${dragging ? " is-over" : ""}`}
                onClick={() => input.current?.click()}
                onDragOver={(e) => (e.preventDefault(), setDragging(true))}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                disabled={reading}
              >
                <UploadCloud size={30} aria-hidden="true" />
                <b>{reading ? "Reading…" : "Choose or drop an Excel or CSV file"}</b>
                <span>
                  .xlsx or .csv, with a column for the name and one for the email. Phone, experience, notice period, last working
                  day, CTC and location are picked up too, and any other column is kept. The first sheet is read.
                </span>
              </button>
              <input ref={input} type="file" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={onPick} />
              {fileError && (
                <div className="ic-note crit" role="alert">
                  <AlertTriangle size={15} aria-hidden="true" /> {fileError}
                </div>
              )}
              {sheet && !fileError && (
                <div className="ic-note warn">No rows with a name or email were found in that file.</div>
              )}
              <p className="ic-mute">
                Nothing is added until you review the list and send. The file itself isn’t uploaded — only the rows you keep.
              </p>
            </div>
          ) : (
            <div className="ic-body">
              {/* ── alerts ── */}
              {counts.existing > 0 && (
                <div className="ic-note warn" role="alert">
                  <AlertTriangle size={15} aria-hidden="true" />
                  <span>
                    <b>
                      {counts.existing} {counts.existing === 1 ? "person is" : "people are"} already on this requirement.
                    </b>{" "}
                    They’re unticked and won’t be added or emailed again.{" "}
                    <button type="button" className="ic-link" onClick={() => setFilter("existing")}>Show them</button>
                  </span>
                </div>
              )}
              {counts.duplicate > 0 && (
                <div className="ic-note warn">
                  <AlertTriangle size={15} aria-hidden="true" />
                  <span>
                    {counts.duplicate} row{counts.duplicate === 1 ? " repeats an email" : "s repeat emails"} from earlier in the file —
                    only the first is kept.{" "}
                    <button type="button" className="ic-link" onClick={() => setFilter("duplicate")}>Show</button>
                  </span>
                </div>
              )}
              {trimmed > 0 && (
                <div className="ic-note warn">
                  The file has {trimmed + MAX_ROWS} rows; the first {MAX_ROWS} are shown. Import the rest as a second file.
                </div>
              )}
              {check.isError && (
                <div className="ic-note crit">Couldn’t check who is already on this requirement — anyone already added will be skipped when you send.</div>
              )}

              {/* ── columns ── */}
              <div className="ic-cols">
                <label>
                  <span>Name column</span>
                  <select value={cols?.nameCol ?? -1} onChange={(e) => setCol("nameCol", Number(e.target.value))}>
                    <option value={-1}>— none —</option>
                    {colOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Email column</span>
                  <select value={cols?.emailCol ?? -1} onChange={(e) => setCol("emailCol", Number(e.target.value))}>
                    {colOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                {cols && cols.lastNameCol >= 0 && <span className="ic-mute sm">First + last name joined</span>}
                <button type="button" className="ic-link" onClick={() => setShowCols((v) => !v)} aria-expanded={showCols}>
                  {foundFields.length
                    ? `${foundFields.length} detail column${foundFields.length === 1 ? "" : "s"} found${extraCount ? ` + ${extraCount} other` : ""}`
                    : "Map detail columns"}{" "}
                  {showCols ? "▴" : "▾"}
                </button>
              </div>
              {showCols && (
                <div className="ic-cols ic-cols-more">
                  {IMPORT_FIELDS.map((f) => (
                    <label key={f}>
                      <span>{IMPORT_FIELD_LABEL[f]}</span>
                      <select value={cols?.fields?.[f] ?? -1} onChange={(e) => setFieldCol(f, Number(e.target.value))}>
                        <option value={-1}>— not in file —</option>
                        {colOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </label>
                  ))}
                  {extraCount > 0 && (
                    <label className="ic-check">
                      <input type="checkbox" checked={keepExtra} onChange={(e) => setKeepExtra(e.target.checked)} />
                      <span>Also keep the {extraCount} other column{extraCount === 1 ? "" : "s"} as-is on each candidate</span>
                    </label>
                  )}
                  <p className="ic-mute sm">
                    Set a field to “not in file” to leave it out. Shown to your team only — never used in scoring.
                    {(foundFields.includes("noticePeriodDays") || foundFields.includes("currentCtc") || foundFields.includes("expectedCtc")) &&
                      " Notice period and CTC fields are switched on for this job so they’re kept."}
                  </p>
                </div>
              )}

              {/* ── toolbar ── */}
              <div className="ic-toolbar">
                <div className="ic-chips" role="tablist" aria-label="Filter rows">
                  {(
                    [
                      ["all", "All", counts.all],
                      ["ready", "New", counts.ready],
                      ["existing", "Already added", counts.existing],
                      ["duplicate", "Repeated", counts.duplicate],
                      ["fix", "Needs fixing", counts.fix],
                    ] as const
                  )
                    .filter(([k, , n]) => k === "all" || n > 0)
                    .map(([k, label, n]) => (
                      <button key={k} type="button" role="tab" aria-selected={filter === k} className={filter === k ? "is-on" : ""} onClick={() => setFilter(k)}>
                        {label} <span>{n}</span>
                      </button>
                    ))}
                </div>
                <label className="ic-search">
                  <Search size={14} aria-hidden="true" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" aria-label="Search name or email" />
                </label>
              </div>

              <div className="ic-bulk">
                <label className="ic-check">
                  <input type="checkbox" checked={allVisibleOn} disabled={!visibleSelectable.length} onChange={toggleAll} />
                  <span>Select all{filter !== "all" || q ? " shown" : ""}</span>
                </label>
                <span className="ic-mute sm">
                  {chosen.length} of {counts.ready} selected
                  {check.isFetching && " · checking…"}
                </span>
                <button
                  type="button"
                  className="ic-btn sm danger"
                  disabled={!rows.some((r) => selected.has(r.id))}
                  onClick={() => remove(rows.filter((r) => selected.has(r.id)).map((r) => r.id))}
                >
                  <Trash2 size={13} aria-hidden="true" /> Delete selected
                </button>
              </div>

              {/* ── rows ── */}
              <div className="ic-table" role="table" aria-label="Candidates to import">
                <div className="ic-tr ic-th" role="row">
                  <span role="columnheader" />
                  <span role="columnheader">Name</span>
                  <span role="columnheader">Email</span>
                  <span role="columnheader">Details</span>
                  <span role="columnheader">Status</span>
                  <span role="columnheader" />
                </div>
                {visible.length === 0 && <div className="ic-empty">No rows match.</div>}
                {visible.map((r) => {
                  const s = statusOf.get(r.id)!;
                  const m = existingBy.get(r.email);
                  const can = selectable(r.id);
                  return (
                    <div key={r.id} className={`ic-tr${can ? "" : " is-off"}`} role="row">
                      <span role="cell">
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.name || r.email}`}
                          checked={can && selected.has(r.id)}
                          disabled={!can}
                          onChange={() => toggle(r.id)}
                        />
                      </span>
                      <span role="cell">
                        <input className="ic-cell" value={r.name} placeholder="Name" onChange={(e) => edit(r.id, { name: e.target.value })} aria-label={`Name, row ${r.line}`} maxLength={160} />
                      </span>
                      <span role="cell">
                        <input
                          className={`ic-cell${s === "badEmail" ? " is-bad" : ""}`}
                          value={r.email}
                          placeholder="email@example.com"
                          onChange={(e) => edit(r.id, { email: e.target.value.trim().toLowerCase() })}
                          aria-label={`Email, row ${r.line}`}
                          maxLength={320}
                        />
                      </span>
                      <span role="cell" className="ic-details" title={detailSummary(r)}>
                        {detailSummary(r) || <span className="ic-mute">—</span>}
                      </span>
                      <span role="cell">
                        <span
                          className={`ic-pill ${s}`}
                          title={
                            s === "existing"
                              ? `Already on this requirement${m?.name ? ` as ${m.name}` : ""}${m?.state ? ` (${m.state})` : ""}`
                              : s === "cv"
                                ? "A CV with this email was already uploaded here — adding them links to it."
                                : `Row ${r.line} in the file`
                          }
                        >
                          {STATUS_LABEL[s]}
                        </span>
                      </span>
                      <span role="cell">
                        <button type="button" className="ic-icon-btn" onClick={() => remove([r.id])} aria-label={`Delete row ${r.line}`} data-tip="Delete row" data-tip-pos="left">
                          <Trash2 size={14} />
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* ── what happens ── */}
              <fieldset className="ic-actions">
                <legend>
                  <Mail size={15} aria-hidden="true" /> Each selected candidate is emailed a link to send their CV — free
                </legend>
                <label className="ic-check">
                  <input type="checkbox" checked={needsProfile} onChange={(e) => setNeedsProfile(e.target.checked)} /> Also ask for
                  profile details (phone, experience, notice period…)
                </label>
              </fieldset>
              <fieldset className="ic-actions ic-screen">
                <legend>When their CV comes back</legend>
                {(["suggest", "auto", "off"] as const).map((m) => (
                  <label key={m} className={`ic-option${screen.mode === m ? " is-on" : ""}`}>
                    <input type="radio" name="ic-screen" checked={screen.mode === m} onChange={() => setScreenPick({ ...screen, mode: m })} />
                    <span>
                      <b>{SCREEN_LABEL[m].title}</b>
                      <small>{SCREEN_LABEL[m].help}</small>
                      {screen.mode === m && m !== "off" && (
                        <span className="ic-sub">
                          <label>
                            Bar:{" "}
                            <select
                              value={screen.threshold}
                              onChange={(e) => setScreenPick({ ...screen, threshold: Number(e.target.value) })}
                              aria-label="Minimum resume match score"
                            >
                              {[50, 55, 60, 65, 70, 75, 80, 85, 90].map((t) => (
                                <option key={t} value={t}>{t}% match or more</option>
                              ))}
                            </select>
                          </label>
                        </span>
                      )}
                    </span>
                  </label>
                ))}
                <p className="ic-mute sm">Below the bar nobody is rejected or emailed — they stay on the requirement for you to decide.</p>
              </fieldset>

              {error && <div className="ic-note crit" role="alert">{error}</div>}

              <footer className="ic-foot">
                <button type="button" className="ic-btn" onClick={reset} disabled={busy}>Choose another file</button>
                <button
                  type="button"
                  className="ic-btn primary"
                  disabled={!chosen.length || busy}
                  onClick={submit}
                >
                  {busy ? "Sending…" : `Ask ${chosen.length || ""} for their CV`.replace("  ", " ")}
                </button>
              </footer>
            </div>
          )}
        </div>
      </div>

    </>,
    document.body,
  );
}
